import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const STORAGE_KEY = "yeati_page_favorites";
const MAX_FAVORITES = 5;

type FavoritesContextType = {
  favorites: string[]
  isFavorite: (path: string) => boolean
  toggleFavorite: (path: string) => void
  removeFavorite: (path: string) => void
  isFull: boolean
};

const FavoritesContext = createContext<FavoritesContextType | null>(null);

const loadFavorites = (): string[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed) && parsed.every((item) => typeof item === "string")) {
      return parsed.slice(0, MAX_FAVORITES);
    }
    return [];
  }
  catch {
    return [];
  }
};

export const FavoritesProvider = ({ children }: { children: React.ReactNode }) => {
  const [favorites, setFavorites] = useState<string[]>(loadFavorites);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  }, [favorites]);

  const isFavorite = useCallback(
    (path: string) => favorites.includes(path),
    [favorites],
  );

  const toggleFavorite = useCallback((path: string) => {
    setFavorites((prev) => {
      if (prev.includes(path)) {
        return prev.filter((p) => p !== path);
      }
      if (prev.length >= MAX_FAVORITES) {
        return prev;
      }
      return [...prev, path];
    });
  }, []);

  const removeFavorite = useCallback((path: string) => {
    setFavorites((prev) => prev.filter((p) => p !== path));
  }, []);

  const isFull = favorites.length >= MAX_FAVORITES;

  return (
    <FavoritesContext value={{
      favorites, isFavorite, toggleFavorite, removeFavorite, isFull,
    }}
    >
      {children}
    </FavoritesContext>
  );
};

export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoritesContext);
  if (!context) {
    return {
      favorites: [],
      isFavorite: () => false,
      toggleFavorite: () => {},
      removeFavorite: () => {},
      isFull: false,
    };
  }
  return context;
};
